import { useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEventManagementService } from '../../event-management/services/eventManagementService';
import { RpcError } from '../../../shared/api/rpcClient';
import type { ActionLogEntry } from '../../../types/frontend';

/** JSON-RPC code the backend maps NotFoundError to (`common/jsonrpc.py`). */
const RPC_NOT_FOUND = -32001;

// Query keys for the reports feature (server reads: the report list and each
// rendered report's HTML). Keyed by customer so a customer switch is a distinct
// cache entry, and report HTML is keyed by opsitem so reopening one is instant.
export const reportKeys = {
  list: (customerId: string | undefined) => ['reports', 'list', customerId] as const,
  html: (customerId: string | undefined, opsitemId: string) =>
    ['reports', 'html', customerId, opsitemId] as const,
  entry: (customerId: string | undefined, opsitemId: string | null | undefined) =>
    ['reports', 'entry', customerId, opsitemId] as const,
};

export function useReports(customerId: string | undefined) {
  const service = useEventManagementService();

  const query = useQuery({
    queryKey: reportKeys.list(customerId),
    queryFn: async () => {
      const result = await service.getActionLog(customerId as string, {
        reports_only: true,
        max_results: 50,
      });
      return result.items;
    },
    enabled: !!customerId,
  });

  const { refetch } = query;
  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    entries: (query.data ?? []) as ActionLogEntry[],
    loading: query.isFetching,
    error: query.error ? (query.error instanceof Error ? query.error.message : 'Failed to load reports') : null,
    refresh,
  };
}

/**
 * Resolve one report entry strongly by OpsItem id, for a `?report=oi-...` deep
 * link. A direct get is required rather than a lookup in the list: the list is
 * capped at 50 with no pagination, so an older report is not in it.
 *
 * The error is returned, not swallowed: the reader followed a link to one named
 * report, so a failure has to be said out loud rather than leaving them on an
 * ordinary list wondering. A deleted report is a final answer; anything else
 * (network, authorization, service) gets one more attempt first.
 */
export function useReportEntry(customerId: string | undefined, opsitemId: string | null | undefined) {
  const service = useEventManagementService();

  const query = useQuery({
    queryKey: reportKeys.entry(customerId, opsitemId),
    queryFn: () => service.getActionLogItem(customerId as string, opsitemId as string),
    enabled: !!customerId && !!opsitemId,
    retry: (attempt, error) =>
      attempt < 1 && !(error instanceof RpcError && error.code === RPC_NOT_FOUND),
  });

  return {
    entry: query.data as ActionLogEntry | undefined,
    error: query.error as Error | null,
  };
}

/**
 * Fetch a download URL for a report's PDF, on demand.
 *
 * A mutation rather than a query: the URL is presigned and short-lived, and the
 * first call for a report may render the PDF server-side, so it is fetched when
 * the reader asks for it and never cached.
 */
export function useReportPdfDownload(customerId: string | undefined) {
  const service = useEventManagementService();

  return useMutation({
    mutationFn: (opsitemId: string) => service.getReportPdf(customerId as string, opsitemId),
  });
}

export function useReportHtml(customerId: string | undefined, opsitemId: string) {
  const service = useEventManagementService();

  return useQuery({
    queryKey: reportKeys.html(customerId, opsitemId),
    queryFn: async () => {
      const result = await service.getReport(customerId as string, opsitemId);
      return result.html;
    },
    enabled: !!customerId,
  });
}
